import React, { useState } from 'react';
import { Loader2, DownloadCloud, Package, ShoppingCart, CopyX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';
import EbayProductSyncTable from './EbayProductSyncTable';
import EbaySyncHealth from './EbaySyncHealth';

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-[#111115] border border-white/10 p-4 flex items-center gap-3">
      <span className="w-9 h-9 bg-primary/10 border border-primary/20 flex items-center justify-center">
        <Icon className="w-4 h-4 text-primary" />
      </span>
      <div>
        <div className="text-[11px] text-white/40 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-white font-medium">{value}</div>
      </div>
    </div>
  );
}

export default function EbaySyncPanel({ settings, onSynced }) {
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [lastOrders, setLastOrders] = useState(null);
  const [deduping, setDeduping] = useState(false);
  const { toast } = useToast();

  const removeDuplicates = async () => {
    setDeduping(true);
    try {
      const res = await base44.functions.invoke('ebayRemoveDuplicateListings', {});
      const d = res.data || {};
      if (d.error) {
        toast({ variant: 'destructive', description: d.error });
      } else {
        toast({ description: `Ended ${d.ended} duplicate listing${d.ended === 1 ? '' : 's'}${d.errors?.length ? ` (${d.errors.length} error${d.errors.length === 1 ? '' : 's'})` : ''}.` });
      }
    } catch (e) {
      toast({ variant: 'destructive', description: e?.response?.data?.error || e.message || 'Failed to remove duplicates.' });
    } finally {
      setDeduping(false);
    }
  };

  const syncOrders = async (days = 90) => {
    setSyncingOrders(true);
    setLastOrders(null);
    try {
      const res = await base44.functions.invoke('ebaySyncOrders', { days });
      const d = res.data || {};
      setLastOrders(d);
      if (d.error) {
        toast({ variant: 'destructive', description: d.error });
      } else {
        toast({ description: `Imported ${d.imported || 0} eBay order${d.imported === 1 ? '' : 's'} (${d.found || 0} found, ${d.skipped || 0} already existed).` });
      }
      onSynced?.();
    } catch (e) {
      const message = e?.response?.data?.error || e.message || 'Order sync failed.';
      setLastOrders({ error: message });
      onSynced?.();
      toast({ variant: 'destructive', description: message });
    } finally {
      setSyncingOrders(false);
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : 'Never');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Stat label="Last listing push" value={fmt(settings?.last_listing_sync)} icon={Package} />
        <Stat label="Last order pull" value={fmt(settings?.last_order_sync)} icon={ShoppingCart} />
      </div>

      <EbaySyncHealth settings={settings} />

      <EbayProductSyncTable />

      <div className="bg-[#111115] border border-white/10 p-6">
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Remove Duplicate Listings</h3>
        <p className="text-xs text-white/50 mt-1">
          Scans your live eBay listings and ends older duplicates for any SKU that has more than one active listing, keeping the most recently listed one. Safe to re-run — only true duplicates are touched.
        </p>
        <div className="mt-4">
          <Button onClick={removeDuplicates} disabled={deduping} variant="outline" className="bg-transparent border-white/10 text-white/70 hover:bg-white/5">
            {deduping ? <Loader2 className="w-4 h-4 animate-spin" /> : <CopyX className="w-4 h-4" />}
            Remove duplicate listings
          </Button>
        </div>
      </div>

      <div className="bg-[#111115] border border-white/10 p-6">
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Order Sync</h3>
        <p className="text-xs text-white/50 mt-1">
          Imports eBay orders into the admin orders view, valued at the gross the buyer paid — item plus postage — with eBay's fee captured so the sale can be reconciled. Safe to re-run: it also matches records that arrived under a different source, so nothing is duplicated. Pulls up to 2 years of history (90-day windows, capped per run — re-run to continue). Runs automatically every morning at 6am.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={() => syncOrders(90)} disabled={syncingOrders} className="bg-primary hover:bg-primary/90">
            {syncingOrders ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
            Pull last 90 days
          </Button>
          <Button onClick={() => syncOrders(730)} disabled={syncingOrders} variant="outline" className="bg-transparent border-white/10 text-white/70 hover:bg-white/5">
            {syncingOrders ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
            Pull all (up to 2 years)
          </Button>
        </div>
        {lastOrders && (
          <div className="mt-4 text-xs text-white/60 space-y-1">
            {lastOrders.error ? (
              <div className="text-red-400">Pull failed — {lastOrders.error}</div>
            ) : (
              <div>Imported <span className="text-white">{lastOrders.imported}</span> · found {lastOrders.found} · skipped {lastOrders.skipped} · {lastOrders.days}-day lookback ({lastOrders.windows} window{lastOrders.windows === 1 ? '' : 's'})</div>
            )}
            {lastOrders.message && <div className="text-amber-400">{lastOrders.message}</div>}
          </div>
        )}
      </div>
    </div>
  );
}